import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import useCheckoutAddons from "@app-contexts/public/shared/checkout/hooks/useCheckoutAddons";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import usePublicVenueService from "@app-contexts/public/features/org-profile/data/services/usePublicVenueService";
import ProductAddons from "@app-contexts/public/shared/checkout/components/steps/add-ons/product-add-ons/ProductAddons.web";
import EventAddons from "@app-contexts/public/shared/checkout/components/steps/add-ons/EventAddons/EventAddons.web";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import { DataProvider } from "@shared/constants/common";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";

const ADDONS_PAGE_SIZE = 6;

export interface CartRecommendationsProps {
  showProducts?: boolean;
  showEvents?: boolean;
}

export default function CartRecommendations({
  showProducts = true,
  showEvents = true,
}: CartRecommendationsProps): JSX.Element | null {
  const { t } = useTranslation("checkout");
  const publicBasket = usePublicBasket();
  const { useRetrieve } = usePublicVenueService();
  const { data: venue } = useRetrieve(publicBasket.paymentVenue);
  const { isFlagActive } = useDjangoWaffle();

  const {
    products,
    upsellEvents,
    addonsEligible,
    isProductsLoading,
    allInPricing,
  } = useCheckoutAddons({
    productPageSize: ADDONS_PAGE_SIZE,
  });

  const hasProducts =
    showProducts && (products.length > 0 || isProductsLoading);
  const hasEvents = showEvents && upsellEvents.length > 0;

  if (!addonsEligible || (!hasProducts && !hasEvents)) {
    return null;
  }

  const productsHeading = (
    <Text fontWeight="bold" fontSize="md">
      {t("Add-ons")}
    </Text>
  );

  const eventsHeading = (
    <Text fontWeight="bold" fontSize="md">
      {t("You may be interested in")}
    </Text>
  );

  return (
    <Box>
      {hasProducts && (
        <Flex direction="column" gap="md" mb="lg">
          <ProductAddons.Products
            products={products}
            publicBasket={publicBasket}
            isLoading={isProductsLoading}
            venue={venue}
            analyticsSource="cart_summary"
            heading={productsHeading}
          />
        </Flex>
      )}

      {hasEvents && (
        <SeatingStateProvider
          isOnSeatingMapPage={false}
          disableBestAvailableSeating={
            !isFlagActive(
              "enable_best_assigned_seating",
              publicBasket.paymentVenue
            )
          }
          basketType={DataProvider.Public}
        >
          <Flex direction="column" gap="md" mb="lg">
            <EventAddons.EventsList
              events={upsellEvents}
              allInPricing={allInPricing}
              analyticsSource="cart_summary"
              heading={eventsHeading}
            />
          </Flex>
        </SeatingStateProvider>
      )}
    </Box>
  );
}
